import { useState } from 'react';
import moment from 'moment';
import { PAST_7_DAYS } from 'ui/datePicker/helpers';
import { guessCurrentTimeZone } from 'utils/datetime';
import noop from 'utils/noop';
import { DateType } from 'ui/datePicker/types';

export interface MissionControlFiltersState {
  clearFilters: () => void;
  creatorIds: number[];
  setCreatorIds: (creatorIds: number[]) => void;
  modifierIds: number[];
  setModifierIds: (modifierIds: number[]) => void;
  dateRangePreset: string;
  setDateRangePreset: (preset: string) => void;
  timezone: string;
  setTimezone: (timezone: string) => void;
  startDate: DateType;
  setStartDate: (date: DateType) => void;
  endDate: DateType;
  setEndDate: (date: DateType) => void;
  labelIds: number[];
  setLabelIds: (labelIds: number[]) => void;
  messageTypeIds: number[];
  setMessageTypeIds: (types: number[]) => void;
  selectedFiltersCount: number;
}

export const DEFAULT_MISSION_CONTROL_FILTERS_STATE: MissionControlFiltersState = {
  clearFilters: noop,
  creatorIds: [],
  setCreatorIds: noop,
  modifierIds: [],
  setModifierIds: noop,
  dateRangePreset: PAST_7_DAYS,
  setDateRangePreset: noop,
  timezone: guessCurrentTimeZone(),
  setTimezone: noop,
  startDate: moment().subtract(7, 'd'),
  setStartDate: noop,
  endDate: moment(),
  setEndDate: noop,
  labelIds: [],
  setLabelIds: noop,
  messageTypeIds: [],
  setMessageTypeIds: noop,
  selectedFiltersCount: 0,
};

export const useMissionControlFilters = (): MissionControlFiltersState => {
  const [creatorIds, setCreatorIds] = useState<number[]>(
    DEFAULT_MISSION_CONTROL_FILTERS_STATE.creatorIds,
  );
  const [modifierIds, setModifierIds] = useState<number[]>(
    DEFAULT_MISSION_CONTROL_FILTERS_STATE.modifierIds,
  );
  const [dateRangePreset, setDateRangePreset] = useState<string>(
    DEFAULT_MISSION_CONTROL_FILTERS_STATE.dateRangePreset,
  );
  const [timezone, setTimezone] = useState(DEFAULT_MISSION_CONTROL_FILTERS_STATE.timezone);
  const [startDate, setStartDate] = useState<DateType>(
    DEFAULT_MISSION_CONTROL_FILTERS_STATE.startDate,
  );
  const [endDate, setEndDate] = useState<DateType>(DEFAULT_MISSION_CONTROL_FILTERS_STATE.endDate);
  const [labelIds, setLabelIds] = useState<number[]>(
    DEFAULT_MISSION_CONTROL_FILTERS_STATE.labelIds,
  );
  const [messageTypeIds, setMessageTypeIds] = useState<number[]>(
    DEFAULT_MISSION_CONTROL_FILTERS_STATE.messageTypeIds,
  );

  const selectedFiltersCount =
    creatorIds.length + modifierIds.length + labelIds.length + messageTypeIds.length;

  /**
   * NOTE: This does not currently clear date-related filters.
   */
  const clearFilters = () => {
    setCreatorIds(DEFAULT_MISSION_CONTROL_FILTERS_STATE.creatorIds);
    setModifierIds(DEFAULT_MISSION_CONTROL_FILTERS_STATE.modifierIds);
    setLabelIds(DEFAULT_MISSION_CONTROL_FILTERS_STATE.labelIds);
    setMessageTypeIds(DEFAULT_MISSION_CONTROL_FILTERS_STATE.messageTypeIds);
  };

  return {
    clearFilters,
    dateRangePreset,
    setDateRangePreset,
    timezone,
    setTimezone,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    creatorIds,
    setCreatorIds,
    modifierIds,
    setModifierIds,
    labelIds,
    setLabelIds,
    messageTypeIds,
    setMessageTypeIds,
    selectedFiltersCount,
  };
};
